"use client";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import NextAdapterApp from "next-query-params/app";
import { usePathname, useRouter } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { getHistoryNavigationGuard } from "../../utils/historyNavigationGuard";
import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { QueryParamProvider } from "use-query-params";
import AuthRedirect from "../../components/AuthRedirect";
import CustomTooltip from "../../components/CustomTooltip";
import Dialog from "../../components/Dialog";
import FreeTrialBanner from "../../components/FreeTrialBanner";
import { GlobalErrorBoundary } from "../../components/GlobalErrorBoundary";
import Main from "../../components/Main";
import ModalConfirmDialog from "../../components/ModalConfirmDialog";
import { renderToast } from "../../components/renderToast";
import Spinner from "../../components/Spinner";
import SubscriptionIssueBanner from "../../components/SubscriptionIssueBanner";
import { FailedPaymentWallHost } from "../../components/FailedPaymentWall";
import {
  SupabaseProvider,
  useSupabaseClient,
} from "../../components/SupabaseProvider";
import {
  REFERRAL_CODE_KEY,
  REFERRAL_QUERY_PARAM,
} from "../../const/referral";
import {
  SS_CURATION_KEY,
  SS_CURATION_QUERY_PARAM,
} from "../../const/curation";
import {
  EXPERIMENTAL_KEY,
  EXPERIMENTAL_QUERY_PARAM,
} from "../../const/experimental";

import { ExportProvider } from "../../context/exportContext";
import { AttributionHelper } from "../../helpers/AttributionHelper";
import { initializeVideoElement } from "../../helpers/video";
import { ModalContext, useModal } from "../../hooks/useModal";
import splitbee from "../../lib/splitbee";

// Folded in from app/route-tracking.tsx so PostHog $pageview only fires on
// routes that actually mount AppProviders (i.e. routes where posthog.init has
// run). The marketing layout doesn't include this provider tree, so the
// landing page no longer pulls in posthog-js.
function RouteTracking() {
  const pathname = usePathname();
  useEffect(() => {
    posthog.capture("$pageview");
    AttributionHelper.captureAttribution();
  }, [pathname]);
  return null;
}

// Mirrors `ReferralCapture` in `marketing-providers.tsx` for users that
// land directly on an authenticated route via a referral link, plus fires
// the `Referral Code Found` posthog event (posthog is initialised here
// but not in the marketing tree). Also tracks the legacy `?via=` splitbee
// referral param.
function ReferralCapture() {
  const pathname = usePathname();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);

    const code = params.get(REFERRAL_QUERY_PARAM);
    if (code) {
      localStorage.setItem(REFERRAL_CODE_KEY, code);
      posthog.capture("Referral Code Found", {
        referral_code: code,
        $set: { referred: true },
      });
    }

    if (params.get(SS_CURATION_QUERY_PARAM) === "true") {
      localStorage.setItem(SS_CURATION_KEY, "true");
    }

    if (params.get(EXPERIMENTAL_QUERY_PARAM) === "true") {
      localStorage.setItem(EXPERIMENTAL_KEY, "true");
    }

    const via = params.get("via");
    if (via) splitbee.track(`Referal Via ${via}`);
  }, [pathname]);
  return null;
}

function SupabaseAuthEffects() {
  const supabaseClient = useSupabaseClient();
  const router = useRouter();

  useEffect(() => {
    const { data } = supabaseClient.auth.onAuthStateChange(async (event) => {
      // Detect if current URL is a recovery link
      const url = new URL(window.location.href);
      const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
      const queryParams = url.searchParams;

      const isRecoveryLink =
        hashParams.get("type") === "recovery" ||
        queryParams.get("type") === "recovery";

      if (
        event === "PASSWORD_RECOVERY" ||
        (event === "SIGNED_IN" && isRecoveryLink)
      ) {
        if (window.location.pathname.startsWith("/auth/reset-password")) {
          return;
        }

        router.replace("/auth/reset-password");
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [router, supabaseClient]);

  return null;
}

export default function AppProviders({
  children,
}: {
  children: React.ReactNode;
}) {
  const [queryClient] = useState(() => new QueryClient());
  useEffect(() => { getHistoryNavigationGuard(window); }, []);

  // OneSignal is no longer eagerly initialised here. It lazy-loads via
  // `helpers/onesignal.ts`'s `getOneSignal()` the first time something
  // actually needs it (e.g. `<Main>` calling `OneSignal.login(profile.id)`
  // post-signin, or the user clicking a notify button). Saves the SDK
  // bytes + init network call on every (app)/(admin)/(embed) route.

  useEffect(() => {
    if (typeof window !== "undefined") {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
        api_host: "https://klap.app/ingest",
        autocapture: false,
        capture_pageview: false,
        loaded: (ph) => {
          if (process.env.NODE_ENV === "development") {
            ph.debug();
            // Console access for local flag testing, e.g.:
            //   posthog.featureFlags.overrideFeatureFlags({ flags: { "flag-key": "variant" } })
            //   posthog.featureFlags.overrideFeatureFlags(false) // clear
            (window as any).posthog = ph;
          }
        },
      });
    }
  }, []);

  // Folded in from `pages/_app.tsx`. Patches the global `<video>` element so
  // every player-class element gets `playsInline` etc. set automatically.
  useEffect(() => {
    initializeVideoElement();
  }, []);

  const modal = useModal();

  return (
    <QueryParamProvider adapter={NextAdapterApp}>
      <QueryClientProvider client={queryClient}>
        <PostHogProvider client={posthog}>
          <GlobalErrorBoundary>
            <SupabaseProvider>
              <SupabaseAuthEffects />
              <RouteTracking />
              <ReferralCapture />
              <ModalContext.Provider value={modal}>
                <AuthRedirect>
                  <ExportProvider>
                    <Main>
                      <FreeTrialBanner />
                      <SubscriptionIssueBanner />
                      {children}
                      <Toaster
                        containerClassName="!z-[99999]"
                        toastOptions={{
                          loading: {
                            icon: <Spinner className="w-4 h-4 shrink-0" />,
                          },
                          success: {
                            icon: <CheckIcon className="w-4 h-4 shrink-0" />,
                          },
                          error: {
                            icon: <XMarkIcon className="w-4 h-4 shrink-0" />,
                          },
                          className:
                            "!rounded-xl !bg-tooltip shadow-2xl !shadow-slate-950/20 text-base font-normal font-sans border border-white/10 !text-tooltip-foreground text-sm dark",
                        }}
                      >
                        {renderToast}
                      </Toaster>
                      <CustomTooltip id="global" />
                    </Main>
                    <Dialog
                      ref={modal.ref}
                      isOpen={modal.isOpen}
                      onClose={() => {
                        // Held while the schedule dialog has a write in
                        // flight (useModal.holdRef): Escape, the overlay and
                        // the X do nothing until it lands.
                        if (modal.holdRef.current) return;
                        modal.dismiss();
                      }}
                      className={modal.className}
                      disabledPadding={modal.disabledPadding}
                    >
                      {modal.children}
                    </Dialog>
                    <ModalConfirmDialog />
                    <FailedPaymentWallHost />
                  </ExportProvider>
                </AuthRedirect>
              </ModalContext.Provider>
            </SupabaseProvider>
          </GlobalErrorBoundary>
        </PostHogProvider>
      </QueryClientProvider>
    </QueryParamProvider>
  );
}
